import {DataLabelRenderEventArgs, HitTestInfo, IRenderEngine} from '@mescius/wijmo.chart';
import {WjFlexChartSeries} from '@mescius/wijmo.angular2.chart';
import chroma from 'chroma-js';
import {Globalize} from '@mescius/wijmo';
import {StringUtils} from './strings';

/**
 * Represents a data item used in a waterfall chart.
 * Each data item contains a name, a value associated with the name,
 * and an optional label for additional descriptive information.
 * We use this interface in the 'Loss Ratio' and 'Premium' waterfall charts.
 *
 */
export interface WaterfallChartDataItem {
    name: string;
    value: number;
    label?: string;
}

export class WjCharts {

    // a simple function to create a fill and stroke color from a single color
    // where the stroke around the shape is a bit darker than the fill
    static fillAndStroke(color: string) {
        return { fill: color, stroke: chroma(color).darken(1).hex() };
    }

    // Generali colors come with 'primary' and 'secondary' variants in two flavours
    // (supposedly a darker ('1') and a lighter one ('2')).
    // Source: 2022_GCC_PPT Template_07 07 22_V1.pptx
    static GeneraliPrimaryColors1 = [
        'rgb(194, 27, 23)',
        'rgb(209, 85, 82)',
        'rgb(224, 141, 139)',
        'rgb(235, 182, 180)',
        'rgb(246, 223, 222)'
    ];

    static GeneraliPrimaryColors2 = [
        'rgb(170, 27, 23)',
        'rgb(192, 85, 82)',
        'rgb(212, 141, 139)',
        'rgb(228, 182, 180)',
        'rgb(243, 223, 222)'
    ];

    static GeneraliSecondaryColors1 = [
        'rgb(50, 50, 50)',
        'rgb(102, 102, 102)',
        'rgb(152, 152, 152)',
        'rgb(189, 189, 189)',
        'rgb(226, 226, 226)'
    ];

    static GeneraliSecondaryColors2 = [
        'rgb(112, 112, 112)',
        'rgb(148, 148, 148)',
        'rgb(183, 183, 183)',
        'rgb(209, 209, 209)',
        'rgb(235, 235, 235)'
    ];

    static GeneraliStatusColors = {
        success: 'rgb(0, 177, 106)',
        warning: 'rgb(255, 127, 0)',
        error: 'rgb(255, 0, 0)',
        success2: 'rgb(114, 171, 29)',
        neutral: 'rgb(51, 51, 51)'
    }

    static GeneraliWaterfallChartColors = {
        increase: 'rgb(146, 208, 80)',
        decrease: 'rgb(255, 0, 0)',
        total: 'rgb(31, 78, 121)',
        total2: 'rgb(255, 192, 0)'
    }

    static GeneraliWaterfallChartStyle = {
        start: WjCharts.fillAndStroke('lightblue'),
        intermediateTotal: WjCharts.fillAndStroke(WjCharts.GeneraliWaterfallChartColors.total),
        total: WjCharts.fillAndStroke(WjCharts.GeneraliWaterfallChartColors.total2),
        rising: WjCharts.fillAndStroke(WjCharts.GeneraliWaterfallChartColors.increase),
        falling: WjCharts.fillAndStroke(WjCharts.GeneraliWaterfallChartColors.decrease),
        connectorLines: { stroke: 'blue', 'stroke-dasharray': '3, 1' }
    };

    // waterfall styles for 'loss ratios': increase is RED because it's worse to lose more
    static GeneraliLossRatioWaterfallChartStyle = {
        start: WjCharts.fillAndStroke('lightblue'),
        intermediateTotal: WjCharts.fillAndStroke(WjCharts.GeneraliWaterfallChartColors.total),
        total: WjCharts.fillAndStroke(WjCharts.GeneraliWaterfallChartColors.total2),
        rising: WjCharts.fillAndStroke(WjCharts.GeneraliWaterfallChartColors.decrease),
        falling: WjCharts.fillAndStroke(WjCharts.GeneraliWaterfallChartColors.increase),
        connectorLines: { stroke: 'blue', 'stroke-dasharray': '3, 1' }
    };

    // We mix the colors from the palette a bit to have a better distribution of colors in pie charts.
    // The strategy is to switch between the primary and secondary colors every 2nd color in the palette.
    // That results in better contrasts for adjacent colors. It's, however, completely arbitrary and
    // reflects essentially my personal taste (besides the base palette, that is Generali standard).
    static GeneraliChartPalette = [
        WjCharts.GeneraliPrimaryColors1[0],
        WjCharts.GeneraliSecondaryColors1[3],
        WjCharts.GeneraliPrimaryColors2[1],
        WjCharts.GeneraliSecondaryColors1[4],
        WjCharts.GeneraliPrimaryColors2[2],
        WjCharts.GeneraliSecondaryColors1[0],
        WjCharts.GeneraliPrimaryColors2[3],
        WjCharts.GeneraliSecondaryColors1[1],
        WjCharts.GeneraliPrimaryColors2[4],
        WjCharts.GeneraliSecondaryColors1[2]
    ];

    // For pie charts, we take the palette as is
    static GeneraliPieChartPalette = WjCharts.GeneraliChartPalette;

    // For pie charts that have 3 types of outcomes, from success to error
    static GeneraliValidationPieChartPalette = [
        WjCharts.GeneraliStatusColors.success,
        WjCharts.GeneraliStatusColors.warning,
        WjCharts.GeneraliStatusColors.error
    ]

    /**
     * Column chart item formatter that applies the Generali pie chart palette colors in _value_-order.
     * By default, palette indexes are selected by the index of the _series_ and
     * not the index of the value within a series. That gives boring results, because we usually
     * have only one series, and all columns show the same color.
     */
    static GeneraliColumnChartPaletteItemFormatter(engine: IRenderEngine, ht: HitTestInfo, defaultRenderer: Function) {
        const index = ht.pointIndex % WjCharts.GeneraliChartPalette.length;
        engine.fill = WjCharts.GeneraliChartPalette[index];
        engine.stroke = '';
        defaultRenderer();
    }

    /**
     * Bar chart item formatter that applies the Generali pie chart palette colors in _value_-order.
     * It's essentially the same as GeneraliColumnChartPaletteItemFormatter, but with the index
     * in reversed order, because Wijmmo picks colors from bottom to top, and we would like
     * to have them from top to bottom.
     */
    static GeneraliBarChartPaletteItemFormatter(engine: IRenderEngine, ht: HitTestInfo, defaultRenderer: Function) {
        const index = ht.pointIndex % WjCharts.GeneraliChartPalette.length;
        const pointIndex = ht.series._getLength() - 1 - index;
        engine.fill = WjCharts.GeneraliChartPalette[pointIndex];
        engine.stroke = '';
        defaultRenderer();
    }

    static updateSeriesItemsSource(series: WjFlexChartSeries | undefined, items: any[]) {
        if (series) {
            series.itemsSource = items;
        }
    }

    /**
     * Populates delta labels in a waterfall chart dataset by calculating the difference
     * between the current value and the previous value in the dataset.
     *
     * @param {WaterfallChartDataItem[]} chartData - An array of waterfall chart data items.
     * Each item must contain a `value` property, and will have its `label` property updated.
     */
    static fillWaterfallChartDeltaLabels(chartData: WaterfallChartDataItem[]) {
        let lastLr = 0;
        chartData.forEach(it => {
            it.label = Globalize.format(it.value - lastLr, 'p1');
            lastLr = it.value;
        });
    }

    /**
     * Complementary label formatter for the above `fillWaterfallChartDeltaLabels` function.
     * It formats the delta labels as percentages using `value`, but only if the label is empty.
     */
    static waterfallChartDeltaLabelsFunction(event: DataLabelRenderEventArgs) {
        const item = event.hitTestInfo.item;
        const label = item.label;
        if (StringUtils.isNullOrEmpty(label)) {
            event.text = Globalize.format(item.value, 'p1');
        }
    }
}
